import React, { useId, useRef, useState } from 'react';
import { toast } from 'sonner';
import { linkCoachCode, previewCoachCode } from './services/onboardingServices';
import { Badge, Btn, Card, ErrorState, Field, Input, Overline } from '../../ForBrands/ui/BrandUI';
import radioChecked from '../../../assets/brand/onboarding/radio-checked-20.svg';
import {
  allCodeTeamsLinked, codeProblem, codeTeamKey, codeTeamLinkArgs, codeTeamMeta, codeTeamTitle, codeWouldAdd, defaultCodeTeam,
  codeRefusal, fillNoteOf, pickedCodeTeam, programPickNote,
} from './codeTeams';
import '../../ForBrands/brandMode.css';

/*
 * Verify a team with its team code (one code per team, which every coach of
 * the team shares; a coach's older code still works), in three beats:
 *   1. "Verify a team" (or "Verify another team" once verified) opens the code field
 *   2. "Check code" shows the team and coach the code belongs to
 *   3. "Confirm" links it: the athlete's first team, or one more team beside it
 *
 * Used by the classic onboarding wizard (autoOpen, onSkip), the classic
 * profile's Verification step and the V2 athlete edit hub. It always renders
 * in the V2 style (BrandUI plus brandMode.css, bm-root on its own wrapper so
 * it reads right outside a V2 shell). The old `v2` prop is still accepted and
 * no longer changes anything. Athletes can be on several rosters (school team
 * plus travel team), so a code for a different team is added, not rejected.
 * A program code (one code for every age group of a program, 10/19 notes)
 * lists the program's squads of the athlete's gender, each titled with its
 * age group and carrying its own problem; the athlete picks theirs.
 */

// A 7px status dot in a token colour, for an outline Badge.
const TokenDot = ({ className }) => <span aria-hidden="true" className={`h-[7px] w-[7px] shrink-0 rounded-full ${className}`} />;

// The V2 option card (same look as OptionCard in the athlete onboarding
// pieces) as a real radio: role, aria-checked, a disabled state and one tab
// stop for the group (the picked team) with arrow keys moving the pick.
const TeamOption = ({ team, selected, tabbable, buttonRef, onSelect, onKeyDown }) => {
  const sub = codeTeamMeta(team);
  // A squad this code cannot link for the athlete (its own problem) says
  // so; the reason shows once it is picked.
  const blocked = !team.alreadyLinked && !!team.problem;
  return (
    <button
      ref={buttonRef}
      type="button"
      role="radio"
      aria-checked={selected}
      disabled={!!team.alreadyLinked}
      tabIndex={tabbable ? 0 : -1}
      onClick={onSelect}
      onKeyDown={onKeyDown}
      className={`flex w-full items-start gap-[14px] rounded-[12px] bg-white p-4 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
        selected ? 'border-2 border-bm-orange' : 'border border-bm-border hover:border-bm-strong'
      }`}
    >
      {selected
        ? <img src={radioChecked} alt="" className="h-5 w-5 shrink-0" />
        : <span className="h-5 w-5 shrink-0 rounded-full border-[1.5px] border-bm-strong bg-white" />}
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="bm-h-s text-bm-ink">{codeTeamTitle(team)}</span>
        {sub && <span className="bm-body-m text-bm-muted">{sub}</span>}
      </span>
      {team.alreadyLinked && <Badge tone="passive" className="shrink-0">Already linked</Badge>}
      {blocked && <Badge tone="critical" className="shrink-0">Cannot link</Badge>}
    </button>
  );
};

// The preview's status: a badge on the team summary, and the line under it.
// `needsPick`: the code covers several teams and none is picked yet. A
// program code answers per squad: the picked squad's problem and whether it
// is added beside the main team (codeProblem, codeWouldAdd).
const statusOf = (preview, chosen, teams, needsPick) => {
  const problem = needsPick ? null : codeProblem(preview, chosen);
  if (problem) return { badge: <Badge tone="critical">Cannot link</Badge>, problem };
  if (needsPick && allCodeTeamsLinked(teams)) {
    return { badge: <Badge tone="passive">Already verified</Badge>, note: 'You are already verified on every team this code covers.' };
  }
  if (needsPick) return { badge: <Badge tone="passive">Pick your team</Badge>, note: 'Pick your team to continue.' };
  if (chosen?.alreadyLinked) {
    return { badge: <Badge tone="passive">Already verified</Badge>, note: 'You are already verified on this team.' };
  }
  if (codeWouldAdd(preview, chosen)) {
    return {
      badge: <Badge><TokenDot className="bg-bm-positive" />Adds a team</Badge>,
      note: `Your profile keeps ${preview.yourTeam || 'your current team'} as your team. ${chosen ? codeTeamTitle(chosen) : 'This team'} will be added as another roster you play on.`,
    };
  }
  return {
    badge: <Badge><TokenDot className="bg-bm-orange" />Ready to verify</Badge>,
    note: "Confirming verifies your profile and links it to this coach's team.",
  };
};

const CoachCodeVerifier = ({
  verifiedTeams = [],
  autoOpen = false,
  onLinked,
  onSkip,
  skipLabel = 'Skip and finish',
}) => {
  const [open, setOpen] = useState(autoOpen);
  const [code, setCode] = useState('');
  const [preview, setPreview] = useState(null);
  // The picked team's key (id and league, see codeTeams.js): squads of one
  // program share the id, so the id alone would pick all of them.
  const [pick, setPick] = useState('');
  const [checking, setChecking] = useState(false);
  const [linking, setLinking] = useState(false);
  const hintId = useId();
  const pickerId = useId();
  const optionRefs = useRef({});

  const hasTeams = (verifiedTeams || []).length > 0;

  const reset = () => {
    setCode('');
    setPreview(null);
    setPick('');
  };

  const check = async () => {
    const clean = code.trim().toUpperCase();
    if (clean.length < 6) {
      toast.error('Enter the six-character code your coach gave you.');
      return;
    }
    setChecking(true);
    try {
      const data = await previewCoachCode(clean);
      setPreview(data);
      const first = defaultCodeTeam(data.teams || []);
      setPick(first ? codeTeamKey(first) : '');
    } catch (err) {
      toast.error(codeRefusal(err, 'Invalid or unverified code.'));
    } finally {
      setChecking(false);
    }
  };

  const confirm = async () => {
    if (!preview || needsPick || linking) return;
    setLinking(true);
    try {
      const data = await linkCoachCode(preview.code, ...codeTeamLinkArgs(chosen));
      toast.success(
        data?.addedAsAdditionalTeam
          ? `Added to ${data.teamName || 'the team'}.`
          : data?.teamName
            ? `Verified with ${data.teamName}.`
            : 'Linked to your coach.',
      );
      // The team said another gender than the profile keeps: it kept its own.
      const note = fillNoteOf(data);
      if (note) toast.info(note);
      reset();
      setOpen(false);
      if (onLinked) onLinked(data);
    } catch (err) {
      toast.error(codeRefusal(err, 'Could not link this code.'));
    } finally {
      setLinking(false);
    }
  };

  const teams = preview?.teams || [];
  // No team starts picked on a code for several teams unless the athlete's
  // own squad is known (defaultCodeTeam): Confirm waits for their pick.
  const chosen = preview ? pickedCodeTeam(teams, pick) : null;
  const needsPick = teams.length > 1 && !chosen;
  const status = preview ? statusOf(preview, chosen, teams, needsPick) : null;
  // Before a pick the summary names the program (a program code's name, or
  // the name every row shares: the squads of one program), else asks for a
  // pick.
  const names = [...new Set(teams.map((t) => (t.teamName || '').trim()).filter(Boolean))];
  const program = String(preview?.programName || '').trim();
  const summaryName = chosen ? codeTeamTitle(chosen) : needsPick ? (program || (names.length === 1 ? names[0] : 'Pick your team below')) : 'Team';
  const pickLabel = programPickNote(preview) || 'This coach covers more than one team. Pick yours:';

  // Radio group keyboard support, as the old native radios had: Tab reaches
  // the picked team only (the first team that can be linked while none is
  // picked), and the arrow keys move the pick between the teams that can
  // still be linked, from the picked team or else the focused one. With Alt,
  // Ctrl or Cmd held the arrows stay the browser's (Alt+Left and Cmd+Left go
  // back a page), as the onboarding team picker (TeamSteps) leaves them.
  const pickable = teams.filter((t) => !t.alreadyLinked);
  const tabStop = pickable.find((t) => codeTeamKey(t) === pick) || pickable[0];
  const onPickerKey = (e, focused) => {
    const step = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 }[e.key];
    if (!step || e.altKey || e.ctrlKey || e.metaKey || !pickable.length) return;
    e.preventDefault();
    const from = pick || (focused ? codeTeamKey(focused) : '');
    const at = pickable.findIndex((t) => codeTeamKey(t) === from);
    const next = at < 0
      ? pickable[step > 0 ? 0 : pickable.length - 1]
      : pickable[(at + step + pickable.length) % pickable.length];
    setPick(codeTeamKey(next));
    optionRefs.current[codeTeamKey(next)]?.focus();
  };

  if (!open) {
    return (
      <div className="bm-root flex flex-wrap items-center gap-2">
        <Btn onClick={() => setOpen(true)}>
          {hasTeams ? 'Verify another team' : 'Verify a team'}
        </Btn>
        {onSkip && <Btn variant="secondary" onClick={onSkip}>{skipLabel}</Btn>}
      </div>
    );
  }

  if (!preview) {
    return (
      <Card padding="p-5" className="bm-root flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <Field label="Team code">
            <Input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase().slice(0, 8))}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); check(); } }}
              placeholder="ABC123"
              maxLength={8}
              aria-describedby={hintId}
              className="max-w-xs !h-12 text-center !font-archivo !text-[20px] !font-bold tracking-[0.3em]"
              autoFocus
            />
          </Field>
          <p id={hintId} className="bm-body-s text-bm-muted">
            Your coach or program shares one code per team. You see the team before anything is linked.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Btn onClick={check} disabled={checking || code.trim().length < 6}>
            {checking ? 'Checking...' : 'Check code'}
          </Btn>
          {(hasTeams || !autoOpen) && (
            <Btn variant="secondary" onClick={() => { reset(); setOpen(false); }}>Cancel</Btn>
          )}
          {onSkip && <Btn variant="secondary" onClick={onSkip}>{skipLabel}</Btn>}
        </div>
      </Card>
    );
  }

  const chosenSub = codeTeamMeta(chosen, [
    chosen?.partnerProgram && chosen.partnerProgram !== chosen.leagueLabel ? chosen.partnerProgram : '',
  ]);

  return (
    <Card padding="p-5" className="bm-root flex flex-col gap-4">
      <section aria-label="Team for this code" className="flex flex-col gap-1 rounded-[12px] border border-bm-border bg-bm-subtle p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Overline>Code {preview.code}</Overline>
          {status.badge}
        </div>
        <div className="bm-h-s text-bm-ink">{summaryName}</div>
        {chosenSub && <div className="bm-body-m text-bm-muted">{chosenSub}</div>}
        <div className="bm-body-m text-bm-ink break-words pt-1">
          Coach: <span className="bm-label-l">{(chosen && chosen.coachName) || preview.coachName || preview.coachEmail || 'your coach'}</span>
        </div>
      </section>

      {teams.length > 1 && (
        <div className="flex flex-col gap-2">
          <p id={pickerId} className="bm-label-l text-bm-ink">{pickLabel}</p>
          <div role="radiogroup" aria-labelledby={pickerId} className="flex flex-col gap-2">
            {teams.map((t) => (
              <TeamOption
                key={codeTeamKey(t)}
                team={t}
                selected={pick === codeTeamKey(t)}
                tabbable={t === tabStop}
                buttonRef={(el) => { optionRefs.current[codeTeamKey(t)] = el; }}
                onSelect={() => setPick(codeTeamKey(t))}
                onKeyDown={(e) => onPickerKey(e, t)}
              />
            ))}
          </div>
        </div>
      )}

      {status.problem ? (
        <div role="alert"><ErrorState>{status.problem}</ErrorState></div>
      ) : (
        <p className="bm-body-m text-bm-muted">{status.note}</p>
      )}

      <div className="flex flex-wrap gap-2">
        <Btn onClick={confirm} disabled={linking || !!status.problem || needsPick || !!chosen?.alreadyLinked}>
          {linking ? 'Linking...' : codeWouldAdd(preview, chosen) ? 'Confirm and add team' : 'Confirm and verify'}
        </Btn>
        <Btn variant="secondary" onClick={reset} disabled={linking}>Use a different code</Btn>
      </div>
    </Card>
  );
};

export default CoachCodeVerifier;
